import { appQueryClient } from "@/app/providers";
import { create } from "zustand";
import { isAxiosError } from "axios";
import { normalizeAdminSettings } from "@/api/settings";
import { bindHttpContext, http } from "@/lib/http";
import { normalizeUser } from "@/lib/normalizers";
import { clearToken, clearVaultView, loadToken, loadVaultView, saveToken, saveVaultView } from "@/lib/storage";
import { buildBaseUrl, getTenantContext, type TenantContext } from "@/lib/tenant";
import type { Settings, User } from "@/types/app";

interface AuthState {
  started: boolean;
  loading: boolean;
  token: string;
  user: User | null;
  customerView: boolean;
  siteSettings: Settings | null;
  invalidTenantPath: boolean;
  tenantContext: TenantContext;
  baseUrl: string;
  searchQuery: string;
  start: () => Promise<void>;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: (tokenOverride?: string) => Promise<User | null>;
  updateSiteSettings: (settings: Settings | null) => void;
  setSearchQuery: (value: string) => void;
  setCustomerView: (enabled: boolean) => void;
  resolveAppPath: (path: string) => string;
  isAuthenticated: () => boolean;
  isTenantAdmin: () => boolean;
  isAdmin: () => boolean;
  isPlatformAdmin: () => boolean;
  isCentralDomain: () => boolean;
  isUnsubscribed: () => boolean;
  isCustomerView: () => boolean;
}

const initialTenantContext =
  typeof window === "undefined"
    ? { subdomain: "", usesPathTenantRouting: false, currentHost: "", currentOrigin: "" }
    : getTenantContext(window.location);

function sanitizeHexColor(value: string | null | undefined, fallback: string) {
  const candidate = String(value ?? "").trim();
  return /^#([0-9a-fA-F]{6})$/.test(candidate) ? candidate : fallback;
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const int = Number.parseInt(normalized, 16);

  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

function toRgba(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function applyTheme(settings: Settings | null) {
  const root = document.documentElement;
  const accentColor = sanitizeHexColor(settings?.accentColor, "#7885ff");
  const accentSoftColor = sanitizeHexColor(settings?.accentSoftColor, "#9aa2ff");
  const secondaryColor = sanitizeHexColor(settings?.secondaryColor, "#9b8cff");
  const surfaceColor = sanitizeHexColor(settings?.surfaceColor, "#181c33");
  const backgroundFromColor = sanitizeHexColor(settings?.backgroundFromColor, "#0a0c16");
  const backgroundToColor = sanitizeHexColor(settings?.backgroundToColor, "#06070d");

  root.dataset.theme = settings?.colorTheme === "light" ? "light" : "dark";
  root.style.setProperty("--brand-accent", accentColor);
  root.style.setProperty("--brand-accent-soft", accentSoftColor);
  root.style.setProperty("--brand-secondary", secondaryColor);
  root.style.setProperty("--surface-color", surfaceColor);
  root.style.setProperty("--page-bg-start", backgroundFromColor);
  root.style.setProperty("--page-bg-end", backgroundToColor);
  root.style.setProperty("--border", toRgba(accentColor, 0.18));
  root.style.setProperty("--ring", toRgba(accentColor, 0.42));
  root.style.setProperty("--surface-glass-top", toRgba(surfaceColor, 0.88));
  root.style.setProperty("--surface-glass-bottom", toRgba(backgroundToColor, 0.82));
  root.style.setProperty("--grid-line", toRgba(accentSoftColor, 0.06));
  root.style.setProperty("--hero-orb-primary", toRgba(accentColor, 0.32));
  root.style.setProperty("--hero-orb-secondary", toRgba(secondaryColor, 0.22));
}

function resolveCustomerView(tenant: string, user: User | null) {
  if (!tenant || !user || !(user.roleId === 1 || user.isAdmin)) {
    return false;
  }

  return loadVaultView(tenant) === "user";
}

function resolveUserMePath(tenant: string) {
  return tenant ? "/api/v1/user-me" : "/api/v1/user/me";
}

export const useAuthStore = create<AuthState>((set, get) => ({
  started: false,
  loading: false,
  token: "",
  user: null,
  customerView: false,
  siteSettings: null,
  invalidTenantPath: false,
  tenantContext: initialTenantContext,
  baseUrl: buildBaseUrl(initialTenantContext),
  searchQuery: "",
  async start() {
    if (get().loading || get().started) {
      return;
    }

    const tenantContext = getTenantContext(window.location);
    const baseUrl = buildBaseUrl(tenantContext);
    set({ loading: true, tenantContext, baseUrl, invalidTenantPath: false });

    try {
      let siteSettings: Settings | null = null;
      if (tenantContext.subdomain) {
        const siteResponse = await http.get("/api/v1/site-info");
        siteSettings = normalizeAdminSettings((siteResponse.data?.data ?? {}) as Record<string, unknown>) as Settings;
      }

      const token = loadToken(tenantContext.subdomain);
      let user: User | null = null;
      let customerView = false;

      if (token) {
        user = await get().refreshUser(token);
        customerView = resolveCustomerView(tenantContext.subdomain, user);
      }

      applyTheme(siteSettings);
      set({ token, user, siteSettings, customerView, started: true, loading: false, invalidTenantPath: false });
    } catch (error) {
      const invalidTenantPath = Boolean(
        tenantContext.subdomain
        && isAxiosError(error)
        && error.response?.status === 404,
      );

      applyTheme(null);
      set({
        started: true,
        loading: false,
        token: "",
        user: null,
        customerView: false,
        siteSettings: null,
        invalidTenantPath,
      });
    }
  },
  async login(login, password) {
    appQueryClient.clear();
    const response = await http.post("/api/v1/auth/login", { login, password });
    const nextToken = response.data?.data?.access_token ?? "";
    const tenant = get().tenantContext.subdomain;

    if (nextToken) {
      saveToken(tenant, nextToken);
    }

    let user: User | null = null;
    let customerView = false;
    if (nextToken) {
      user = await get().refreshUser(nextToken);
      customerView = resolveCustomerView(tenant, user);
    }

    set({ token: nextToken, user, customerView });
  },
  async logout() {
    const { tenantContext, token } = get();
    if (token) {
      try {
        await http.post("/api/v1/auth/logout", {});
      } catch {}
    }

    clearToken(tenantContext.subdomain);
    clearVaultView(tenantContext.subdomain);
    appQueryClient.clear();
    set({ token: "", user: null, customerView: false });
  },
  async refreshUser(tokenOverride) {
    const token = tokenOverride ?? get().token;
    const tenant = get().tenantContext.subdomain;

    if (!token) {
      set({ user: null });
      return null;
    }

    const userResponse = await http.get(resolveUserMePath(tenant), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const user = normalizeUser((userResponse.data?.data ?? null) as Record<string, unknown> | null);
    set({ user });
    return user;
  },
  updateSiteSettings(settings) {
    applyTheme(settings);
    set({ siteSettings: settings });
  },
  setSearchQuery(value) {
    set({ searchQuery: value });
  },
  setCustomerView(enabled) {
    const tenant = get().tenantContext.subdomain;
    if (!get().isTenantAdmin() || !tenant) {
      set({ customerView: false });
      return;
    }

    saveVaultView(tenant, enabled ? "user" : "creator");
    set({ customerView: enabled });
  },
  resolveAppPath(path) {
    const prefix = get().tenantContext.usesPathTenantRouting && get().tenantContext.subdomain ? `/t/${get().tenantContext.subdomain}` : "";
    if (!path.startsWith("/")) {
      return `${prefix}/${path}`;
    }

    return `${prefix}${path}`;
  },
  isAuthenticated: () => Boolean(get().token),
  isTenantAdmin: () => Boolean(get().user?.roleId === 1 || get().user?.isAdmin),
  isAdmin: () => get().isTenantAdmin() && !get().customerView,
  isPlatformAdmin: () => get().isCentralDomain() && Boolean(get().user?.isAdmin),
  isCentralDomain: () => !get().tenantContext.subdomain,
  isUnsubscribed: () => !get().isAdmin() && !get().user?.isSubscribed,
  isCustomerView: () => get().customerView,
}));

bindHttpContext(() => {
  const { baseUrl, token } = useAuthStore.getState();
  return { baseUrl, token };
});
